<template>
  <div>
    <v-tabs class="ooliba-tab" active-class="ooliba-tab-active">
      <v-tab>{{ $t("global.business-process.run-info") }}</v-tab>
      <v-tab-item>
        <v-skeleton-loader v-if="busy" type="paragraph" class="pa-4" />

        <span v-else-if="taskStatus === 'PENDING' || !run">
          {{ $t("global.msg.no-run-info") }}
        </span>

        <div v-else>
          <v-card class="run-card mb-3 py-2 px-2">
            <b>{{ $t("global.run.run") }}: </b>

            <router-link :to="runRoute" class="ooliba-font-color-blue">
              {{ run?.title }} (#{{ run?.id }})
            </router-link>
          </v-card>

          <v-row dense class="mb-3">
            <v-col>
              <basic-card-horizontal
                :headers="infoCardHeaders"
                :item="infoCardItem"
                min-width="600"
              >
                <template #[`item.env`]="{ item }">
                  <router-link :to="envRoute"> {{ item.env }} </router-link>
                </template>

                <template #[`item.createdByName`]="{ item }">
                  <span v-if="item.isCreatedByDeleted">
                    {{ $t("global.concepts.deleted-user") }}
                  </span>

                  <router-link
                    v-else
                    class="ooliba-font-color-blue"
                    :to="createdByRoute"
                  >
                    {{ item.createdByName }}
                  </router-link>
                </template>
              </basic-card-horizontal>
            </v-col>

            <v-col>
              <status-card :run="run"> </status-card>
            </v-col>
          </v-row>
        </div>
      </v-tab-item>

      <v-tab>{{ $t("global.business-process.run-history") }}</v-tab>
      <v-tab-item>
        <v-row>
          <v-col>
            <v-btn
              :disabled="selectedRows.length != 2"
              color="primary"
              @click="compareRuns"
              style="float: right"
            >
              {{ $t("global.run.compare-runs") }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <ooliba-basic-table
              v-model="selectedRows"
              :headers="headers"
              :items="items"
              :loading="busy"
              no-pagination
              :show-select="items?.length > 1"
              @row-clicked="onRowClick"
            >
              <template #[`item.id`]="{ item }">
                <router-link
                  :to="historyRunRoute(item.id)"
                  class="ooliba-font-color-blue"
                >
                  {{ item.id }}
                </router-link>
              </template>

              <template #[`item.createdOn`]="{ item }">
                {{ formatDate(item.createdOn) }}
              </template>
            </ooliba-basic-table>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs>
    <div ref="bottomEl"></div>
  </div>
</template>

<script>
import { get } from "@/model/api";
import OolibaBasicTable from "@/components/OolibaBasicTable";
import BasicCardHorizontal from "@/components/BasicCardHorizontal";
import StatusCard from "@/components/run/StatusCard";
import { formatDate } from "@/model/util";

export default {
  name: "TaskRunList",

  props: { flowTaskId: Number, taskStatus: String },

  components: { OolibaBasicTable, BasicCardHorizontal, StatusCard },

  data() {
    return {
      run: undefined,
      headers: [
        { text: "Id", value: "id" },
        { text: this.$t("global.concepts.name"), value: "title" },
        { text: this.$t("global.concepts.created-on"), value: "createdOn" },
        {
          text: this.$t("global.modules.module"),
          value: "module",
          width: "242px",
        },
        {
          text: this.$t("global.concepts.version"),
          value: "version",
          width: "180px",
        },
        {
          text: this.$t("global.concepts.status"),
          value: "status",
          width: "210px",
        },
      ],
      selectedRows: [],
      selectedRunIds: undefined,
      items: [],
      timerId: undefined,
      busy: true,

      infoCardHeaders: [
        { text: this.$t("global.modules.module"), value: "module" },
        { text: this.$t("global.run.simulation"), value: "simulation" },
        { text: this.$t("global.concepts.created-on"), value: "dateCreated" },
      ],
    };
  },

  computed: {
    infoCardItem() {
      return {
        module: this.run?.module + " (v." + this.run?.moduleVersion + ")",
        dateCreated: formatDate(this.run?.dateCreated),
        simulation: this.run?.simulation,
      };
    },

    runRoute() {
      if (!this.run?.id) {
        return {};
      }
      return {
        name: "Run",
        params: {
          runId: this.run?.id,
        },
      };
    },
  },

  methods: {
    formatDate,

    historyRunRoute(runId) {
      if (!runId) {
        return {};
      }
      return {
        name: "Run",
        params: {
          runId: runId,
        },
      };
    },

    compareRuns() {
      if (this.selectedRows.length != 2) {
        return;
      }
      this.$router.push({
        path: "/run-comparison",
        query: { runIds: this.selectedRunIds },
      });
    },

    async getRun(id) {
      if (!id) return;

      return await get("/simulationRun?id=" + id).catch((e) => this.onError(e));
    },

    continuouslyUpdateRun() {
      this.timerId = window.setTimeout(async () => {
        if (this.run?.id) {
          if (!this.deleting) {
            this.run = await this.getRun(this.run?.id);
            this.items[0].status = this.run?.translatedStatus;
          }
          if (!this.$store.getters.isStatusFinished(this.run?.status)) {
            this.continuouslyUpdateRun();
          }
        }
      }, 1800);
    },

    async getRunInfoStatus(runInfoId) {
      return await get("/simulationRun/run-info-status/" + runInfoId).catch(
        (error) => this.onError(error)
      );
    },

    onRowClick(item) {
      const runId = item.id;

      this.$router.push({ name: "Run", params: { runId: runId } });
    },

    async getRuns(flowTaskId) {
      return await get("/flow-task/" + flowTaskId + "/runs").catch((error) => {
        this.onError(error);
      });
    },

    onError(error) {
      this.$store.commit("showError", error);
    },

    async loadRuns() {
      this.busy = true;
      this.items = [];
      const runs = await this.getRuns(this.flowTaskId);

      if (runs) {
        this.run = runs[0];

        runs.forEach(async (value) => {
          const item = {};

          item.infoId = value.infoId;
          item[this.headers[0].value] = value.id;
          item[this.headers[1].value] = value.title;
          item[this.headers[2].value] = value.dateCreated;
          item[this.headers[3].value] = value.module;
          item[this.headers[4].value] = value.moduleVersion;
          item[this.headers[5].value] = value?.translatedStatus;

          let statusIcon = this.$store.getters.getStatusIcon(value.status);
          item.statusColor = statusIcon.statusColor;
          item.statusIcon = statusIcon.statusIcon;

          this.items.push(item);
        });
      }
      this.busy = false;

      setTimeout(() => {
        this.$refs.bottomEl?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    },
  },

  watch: {
    flowTaskId: async function () {
      await this.loadRuns();
    },

    selectedRows() {
      if (this.selectedRows.length > 1 && this.selectedRows.length < 6) {
        this.selectedRunIds = this.selectedRows
          .map((item) => item.id)
          .join(",");
      } else {
        this.selectedRunIds = undefined;
      }
    },
  },

  async created() {
    await this.loadRuns();
    this.continuouslyUpdateRun();
  },

  destroyed() {
    clearTimeout(this.timerId);
  },
};
</script>
